import { useEffect, useRef, useState } from "react";

const pains = [
  {
    icon: "ri-shield-cross-line",
    title: "Lack of local trust",
    desc: "Western brands are unknown quantities in CEE. Without credibility and local relationships, deals stall before they start.",
  },
  {
    icon: "ri-global-line",
    title: "Cultural & negotiation gaps",
    desc: "Eastern European business culture runs by different rules. Missteps cost time, money and reputation.",
  },
  {
    icon: "ri-message-3-line",
    title: "Ineffective messaging",
    desc: "Generic Western messaging fails to resonate. CEE buyers respond to different triggers, proof points and communication styles.",
  },
  {
    icon: "ri-mail-close-line",
    title: "Poor follow-up systems",
    desc: "Leads go cold. Without automated, intelligent follow-up, even warm prospects vanish permanently.",
  },
  {
    icon: "ri-money-euro-circle-line",
    title: "Expensive local hiring",
    desc: "Building a local team takes 6–18 months and significant capital — with no guarantee of results.",
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function Problem() {
  const { ref, inView } = useInView();

  return (
    <section className="bg-[#060606] py-32 px-6 relative overflow-hidden">
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNDOUE4NEMiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTAgMGg0MHY0MEgweiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <div ref={ref} className={`max-w-3xl mb-20 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-px bg-[#C9A84C]/40" />
            <span className="text-[#C9A84C] text-xs tracking-[0.3em] uppercase font-light font-['Outfit']">The Reality</span>
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-light text-white">
            Why most companies <em className="text-white/40 not-italic">fail</em> in<br />
            Eastern Europe
          </h2>
        </div>

        {/* Pain points */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#C9A84C]/8">
          {pains.map((pain, i) => (
            <div
              key={pain.title}
              className={`bg-[#060606] p-10 lg:p-12 group hover:bg-[#0a0a0a] transition-all duration-500 cursor-default ${
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="flex gap-6">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 border border-[#C9A84C]/20 group-hover:border-[#C9A84C]/60 transition-colors duration-300">
                  <i className={`${pain.icon} text-[#C9A84C]/60 group-hover:text-[#C9A84C] text-lg transition-colors duration-300`} />
                </div>
                <div>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-white mb-3">
                    {pain.title}
                  </h3>
                  <p className="text-white/45 text-sm font-light leading-relaxed font-['Outfit']">
                    {pain.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Strong statement */}
        <div className={`mt-16 border-l-2 border-[#C9A84C] pl-8 lg:pl-12 transition-all duration-700 delay-400 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-['Cormorant_Garamond'] text-2xl md:text-3xl lg:text-4xl font-light text-white leading-relaxed">
            Entering Eastern Europe without the right partner is{" "}
            <strong className="text-[#C9A84C] font-light">slow, expensive and risky.</strong>
          </p>
        </div>
      </div>
    </section>
  );
}
